/**
 * 接受邀请页 — 邀请 token + 设密码 → 激活账号 + 展示 TOTP 绑定 QR
 *
 * 注（逻辑不变）：本页只完成 accept-invite（密码 + 账号创建）；TOTP 实际绑定需用户在
 * Authenticator App 扫码后用新密码登录，再调 /auth/bind-totp 完成（MVP 两步走）。
 *
 * 皮肤移植 design/Novel-admin/Login.html 表单 + setup-grid 样式。
 */

import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { acceptInvite } from '~/hooks/use-auth';
import { QrCode } from '~/lib/qr';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const presetEmail = params.get('email') ?? '';
  const presetNickname = params.get('nickname') ?? '';
  const [nickname, setNickname] = useState(presetNickname);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ secret_base32: string; otpauth_uri: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // 密码规则与后端 PasswordSchema 对齐：≥8 位 + 含字母 + 含数字；并要求二次确认一致
  const pwValid = password.length >= 8 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
  const pwMatch = password === confirmPassword;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!nickname || !pwValid || !pwMatch) return;
    setSubmitting(true);
    try {
      const res = await acceptInvite({
        invite_token: token,
        password,
        nickname,
      });
      setResult(res.totp_setup);
    } catch (err) {
      if (err instanceof AdminApiError) toast(err.message, { tone: 'neg' });
      else toast('邀请激活失败', { tone: 'neg' });
    } finally {
      setSubmitting(false);
    }
  };

  // 邀请 token 缺失/损坏（邮件链接被截断或过期）→ 兜底引导，而非让用户填完才报错
  if (!token) {
    return (
      <div className="step">
        <div style={{ marginBottom: 18 }}>
          <h2 className="form-title">邀请链接无效</h2>
          <p className="form-sub">邀请 token 缺失或链接已损坏，请联系管理员重新发送邀请邮件</p>
        </div>
        <Link
          to="/auth/login"
          className="btn-big"
          style={{ textDecoration: 'none', color: 'var(--bg)' }}
        >
          返回登录
        </Link>
      </div>
    );
  }

  if (result) {
    const copySecret = () => {
      if (navigator.clipboard) navigator.clipboard.writeText(result.secret_base32).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    };
    const secretGrouped = result.secret_base32.match(/.{1,4}/g)?.join(' ') ?? result.secret_base32;

    return (
      <div className="step">
        <div className="bind-success">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          账号已激活
        </div>

        <div style={{ marginBottom: 18 }}>
          <h2 className="form-title">绑定双重验证</h2>
          <p className="form-sub">用 Authenticator App 扫描二维码，或手动输入密钥</p>
        </div>

        <div className="setup-grid">
          <div className="qr-frame">
            <QrCode value={result.otpauth_uri} size={136} />
          </div>
          <div className="setup-steps">
            <div className="setup-step">
              <span className="setup-step-num">1</span>
              <div>
                <div className="setup-step-title">下载验证器 App</div>
                <div className="setup-step-desc">Google Authenticator · Authy · 1Password</div>
              </div>
            </div>
            <div className="setup-step">
              <span className="setup-step-num">2</span>
              <div>
                <div className="setup-step-title">扫描二维码</div>
                <div className="setup-step-desc">在 App 中点「添加」→「扫码」</div>
              </div>
            </div>
            <div className="setup-step">
              <span className="setup-step-num">3</span>
              <div>
                <div className="setup-step-title">用新密码登录完成绑定</div>
                <div className="setup-step-desc">登录成功页会要求输入 6 位验证码</div>
              </div>
            </div>
          </div>
        </div>

        <div className="qr-secret-row">
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', flexShrink: 0 }}>
            无法扫码？密钥
          </span>
          <code className="qr-secret">{secretGrouped}</code>
          <button type="button" className="qr-secret-copy" onClick={copySecret}>
            {copied ? (
              <>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                已复制
              </>
            ) : (
              <>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                复制
              </>
            )}
          </button>
        </div>

        <Link
          to="/auth/login"
          className="btn-big"
          style={{ textDecoration: 'none', color: 'var(--bg)' }}
        >
          前往登录
        </Link>
      </div>
    );
  }

  return (
    <div className="step">
      <div style={{ marginBottom: 24 }}>
        <h2 className="form-title">接受邀请</h2>
        <p className="form-sub">设置昵称与登录密码以激活你的运营账户</p>
      </div>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="field">
          <label className="field-label" htmlFor="invite-email">
            邮箱
          </label>
          <input
            id="invite-email"
            className="input-big mono"
            type="email"
            value={presetEmail}
            disabled
            style={{ color: 'var(--fg-muted)', cursor: 'not-allowed' }}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="invite-nickname">
            昵称
          </label>
          <input
            id="invite-nickname"
            className="input-big"
            type="text"
            maxLength={64}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="invite-pw">
            设置密码
          </label>
          <PasswordInput
            id="invite-pw"
            className="input-big"
            placeholder="至少 8 位，含字母+数字"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="invite-pw2">
            确认密码
          </label>
          <PasswordInput
            id="invite-pw2"
            className="input-big"
            placeholder="再次输入密码"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {confirmPassword && !pwMatch ? (
            <span style={{ fontSize: 11.5, color: 'var(--neg)' }}>两次输入的密码不一致</span>
          ) : null}
        </div>
        <button
          type="submit"
          className="btn-big"
          style={{ marginTop: 4 }}
          disabled={submitting || !nickname || !pwValid || !pwMatch}
        >
          {submitting ? '激活中…' : '激活账号'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Link
            to="/auth/login"
            style={{ color: 'var(--fg-muted)', textDecoration: 'none', fontSize: 12.5 }}
          >
            返回登录
          </Link>
        </div>
      </form>
    </div>
  );
}
